<template>
  <section class="tech-research-showcase">
    <div class="container">
      <div class="tech-header text-center">
        <div class="tech-badge">
          <i class="fas fa-microchip"></i>
          <span>{{ content.header.tag }}</span>
        </div>
        <h2>{{ content.header.title }}</h2>
        <p class="tech-subtitle">{{ content.header.subtitle }}</p>
      </div>

      <div class="tech-grid">
        <div class="tech-card ai-card wow fadeInUp" v-for="(tech, index) in content.technologies" :key="index" :data-wow-delay="`${(index + 1) * 0.1}s`">
          <div class="tech-image">
            <img :src="tech.image" :alt="tech.title" />
            <div class="tech-overlay">
              <div class="tech-icon">
                <i :class="tech.icon"></i>
              </div>
            </div>
          </div>
          <div class="tech-content">
            <div class="tech-category">{{ tech.category }}</div>
            <h3>{{ tech.title }}</h3>
            <p>{{ tech.description }}</p>
            <div class="tech-features">
              <span class="feature-tag" v-for="(feature, featureIndex) in tech.features" :key="featureIndex">{{ feature }}</span>
            </div>
            <div class="tech-progress">
              <div class="progress-info">
                <span class="progress-label">Research Progress</span>
                <span class="progress-percent">{{ tech.progress }}%</span>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" :style="`width: ${tech.progress}%`"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ResearchItsTechComp',
  props: {
    content: {
      type: Object,
      required: true
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Tech Research Showcase Section */
.tech-research-showcase {
  padding: 120px 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  position: relative;
  overflow: hidden;
}

.tech-research-showcase::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.03)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  z-index: 1;
}

.tech-header {
  position: relative;
  z-index: 2;
  margin-bottom: 80px;
}

.tech-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
}

.tech-badge i {
  font-size: 16px;
}

.tech-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.tech-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  position: relative;
  z-index: 2;
}

.tech-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
}

.tech-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  border-color: rgba(102, 126, 234, 0.5);
}

.tech-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.tech-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.tech-card:hover .tech-image img {
  transform: scale(1.1);
}

.tech-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.8), rgba(118, 75, 162, 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.tech-card:hover .tech-overlay {
  opacity: 1;
}

.tech-icon {
  font-size: 48px;
  color: white;
}

.tech-content {
  padding: 30px;
}

.tech-category {
  color: #667eea;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
}

.tech-content h3 {
  color: white;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 15px;
  line-height: 1.3;
}

.tech-content p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.tech-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 25px;
}

.feature-tag {
  background: rgba(102, 126, 234, 0.2);
  color: #667eea;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgba(102, 126, 234, 0.3);
}

.tech-progress {
  margin-top: 20px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
}

.progress-percent {
  color: #667eea;
  font-size: 14px;
  font-weight: 600;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  transition: width 0.4s ease;
}

/* Responsive Design */
@media (max-width: 768px) {
  .tech-header h2 {
    font-size: 36px;
  }
  
  .tech-grid {
    grid-template-columns: 1fr;
  }
  
  .tech-research-showcase {
    padding: 80px 0;
  }
}
</style>
